import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkEntries, fetchWorkEntriesProjectTracking } from "../../../store/slices/workentriesSlice";
import { fetchDescriptionTasks, fetchDescriptionTasksProjectTracking } from "../../../store/slices/DescriptionTask";
import { fetchHardwareDescriptions, fetchHardwareProjectTracking } from "../../../store/slices/hardwareDescriptionSlice";
import { fetchEmployees } from "../../../store/slices/employeeSlice";
import { fetchAllProjects } from "../../../store/slices/projectsSlice";

const useMatchedOperationsData = (rowProjects = []) => {

    const dispatch = useDispatch();

    const { employees } = useSelector(state => state.employees);
    const { projects } = useSelector(state => state.projects);
    const { entries, projectTrackingWorkEntries } = useSelector(state => state.workentries);
    const { items, hardwareProjectTracking } = useSelector(state => state.hardwareDescription);
    const { tasks, descriptionProjectTracking } = useSelector(state => state.descriptionTasks);

    useEffect(() => {
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
        dispatch(fetchHardwareProjectTracking());
        dispatch(fetchWorkEntries());
        dispatch(fetchHardwareDescriptions());
        dispatch(fetchDescriptionTasks());
        dispatch(fetchEmployees());
        dispatch(fetchAllProjects());
    }, [dispatch]);

    const [matchedProjects, setMatchedProjects] = useState([]);
    const [allrowData, setAllrowData] = useState([]);
    const [unifitedList, setUnifitedList] = useState([]);

    useEffect(() => {
        if (projects?.length && rowProjects?.length) {
            const mapped = rowProjects.map((rowProject) => {
                const old = projects.find((pro) => {
                    if (rowProject?.quotationId != null && pro?.quotationId != null) {
                        return pro?.TypeService === rowProject?.TypeService && String(pro.quotationId) === String(rowProject.quotationId);
                    }
                    return pro?.TypeService === rowProject?.TypeService && pro.ProjectName === rowProject.ProjectName;
                });

                if (!old) return null;

                const isSame =
                    old._id === rowProject.oldprojectid &&
                    (old.quotationId != null && rowProject.quotationId != null ? String(old.quotationId) === String(rowProject.quotationId) : old.ProjectName === rowProject.ProjectName) &&
                    old.TypeService === rowProject.TypeService;

                return {
                    _id: rowProject._id,
                    ProjectName: rowProject.ProjectName,
                    TypeService: rowProject.TypeService,
                    quotationId: rowProject.quotationId ?? old.quotationId ?? null,
                    oldProject: old,
                    newProject: isSame ? old : rowProject,
                };
            }).filter(Boolean);

            setMatchedProjects(mapped);
        }
    }, [projects, rowProjects]);

    useEffect(() => {
        if (!matchedProjects.length) return;

        const updated = matchedProjects.map((proj) => {
            const oldId = proj.oldProject._id;
            const newId = proj.newProject._id;

            return {
                _id: newId,
                TypeService: proj.newProject.TypeService,
                ProjectName: proj.newProject.ProjectName,
                Assignedperson: proj.newProject.Assignedperson,

                oldProject: {
                    ...proj.oldProject,
                    oldworkEntries: entries.filter((e) => e.projectid === oldId),
                    oldDescriptions: tasks.filter((t) => t.projectid === oldId),
                    oldHardwareParts: items.filter((i) => i.projectid === oldId),
                },

                newProject: {
                    ...proj.newProject,
                    newworkEntries: projectTrackingWorkEntries.filter((e) => e.projectid === newId),
                    newDescriptions: descriptionProjectTracking.filter((t) => t.projectid === newId),
                    newHardwareParts: hardwareProjectTracking.filter((i) => i.projectid === newId),
                },
            };
        });

        setAllrowData(updated);
    }, [
        matchedProjects,
        entries,
        tasks,
        items,
        projectTrackingWorkEntries,
        descriptionProjectTracking,
        hardwareProjectTracking
    ]);

      
  const processHardwareDescriptions = ({
    allDescriptions,
    employees,
    currentEmpId,
  }) => {
    const empMap = employees.reduce((acc, e) => {
      acc[e._id] = e;
      return acc;
    }, {});

    const grouped = allDescriptions.reduce((acc, part) => {
      if (!acc[part.description]) acc[part.description] = [];
      acc[part.description].push(part);
      return acc;
    }, {});

    const result = [];

    Object.values(grouped).forEach(parts => {
      const pmParts = parts.filter(p =>
        empMap[p.taskownerid]?.customRoles?.includes("project manager")
      );

      const opParts = parts.filter(p =>
        empMap[p.taskownerid]?.customRoles?.includes("operations") &&
        !empMap[p.taskownerid]?.customRoles?.includes("project manager")
      );

      const highestPM = pmParts.length
        ? pmParts.reduce((a, b) => (b.value > a.value ? b : a))
        : null;

      const highestOP = opParts.length
        ? opParts.reduce((a, b) => (b.value > a.value ? b : a))
        : null;

      // 🔹 Operations keep their own
      opParts.forEach(p => {
        if (p.taskownerid === currentEmpId) {
          result.push(p);
        }
      });

      // 🔹 PM logic
      const isPM = empMap[currentEmpId]?.customRoles?.includes("project manager");
      if (!isPM) return;

      if (!highestPM && highestOP) {
        result.push({ ...highestOP });
        return;
      }

      if (
        highestPM &&
        (!highestOP || highestPM.value >= highestOP.value)
      ) {
        if (highestPM.taskownerid === currentEmpId) {
          result.push(highestPM);
        }
        return;
      }

      if (highestPM && highestOP && highestOP.value > highestPM.value) {
        result.push({ ...highestOP });
      }
    });

    return result.filter(
      (v, i, self) =>
        i === self.findIndex(x => x.description === v.description)
    );
  };

    useEffect(() => {
        if (allrowData?.length && employees?.length) {
            const mergedData = allrowData.map((project) => {
                const assignedData = project.newProject.Assignedperson.map((person) => {
                    // 1️⃣ Find matching employee by email
                    const emp = employees.find((e) => e.EmpEmail === person.email);

                    if (!emp) return null; // skip if no match

                    // 2️⃣ Collect role + id
                    const empid = emp._id;
                    const role = emp.customRoles?.[0] || null;
                    const name = `${emp.EmpFirstName} ${emp.EmpLastName}`;

                    // 3️⃣ Match old/new work entries and descriptions by taskownerid
                    const oldworkEntries = project.oldProject.oldworkEntries.filter(
                        (entry) => entry.taskownerid === empid
                    );
                    const newworkEntries = project.newProject?.newworkEntries.filter(
                        (entry) => entry.taskownerid === empid
                    );

                    const oldDescriptions = project.oldProject.oldDescriptions.filter(
                        (desc) => desc.taskownerid === empid
                    );

                          let newDescriptions = [];

          if (project.newProject?.TypeService === "Hardware") {
            newDescriptions = processHardwareDescriptions({
              allDescriptions: project.newProject?.newDescriptions || [],
              employees,
              currentEmpId: empid,
            });
          } else {
            newDescriptions = project.newProject?.newDescriptions.filter(
              (desc) => desc.taskownerid === empid
            );
          }
                    // const newDescriptions = project.newProject?.newDescriptions.filter(
                    //     (desc) => desc.taskownerid === empid
                    // );

                    const oldHardwareParts = project.oldProject?.oldHardwareParts.filter(
                        (desc) => desc.taskownerid === empid
                    );
                    const newHardwareParts = project.newProject.newHardwareParts.filter(
                        (desc) => desc.taskownerid === empid
                    );

                    return {
                        TypeService: project?.TypeService,
                        // Assignperson: project.Assignperson,
                        name,
                        empid,
                        role,
                        oldworkEntries,
                        newworkEntries,
                        oldDescriptions,
                        newDescriptions,
                        oldHardwareParts,
                        newHardwareParts,
                        matchedPerson: person,
                    };
                }).filter(Boolean) // remove nulls (non-matching employees)
                    .sort((a, b) => (a?.role === "project manager" ? -1 : b?.role === "project manager" ? 1 : 0));

                const managerData = assignedData.filter((emp) => emp?.role === "project manager");
                const employeeData = assignedData.filter((emp) => emp?.role === "operations");

                      if (project.newProject?.TypeService === "Hardware") {

          // 1️⃣ Create employee map for quick lookup
          const empMap = assignedData.reduce((acc, e) => {
            acc[e.empid] = e;
            return acc;
          }, {});

          // 2️⃣ Collect ALL newHardwareParts
          const allNewHardwareParts = assignedData.flatMap(
            e => e.newHardwareParts || []
          );

          // 3️⃣ Group by assemblypartdescription
          const groupedByAssembly = allNewHardwareParts.reduce((acc, part) => {
            const key = part.assemblepartdescription;
            if (!acc[key]) acc[key] = [];
            acc[key].push(part);
            return acc;
          }, {});

          // 4️⃣ Reset processed hardware array
          assignedData.forEach(e => {
            e.hardwarePartsOngoing = [];
          });

          // 5️⃣ Find Project Manager
          const pmEmp = assignedData.find(
            e => e.role === "project manager"
          );

          // 6️⃣ Apply rules
          Object.entries(groupedByAssembly).forEach(([desc, parts]) => {
            if (!pmEmp) return;

            const pmParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "project manager"
            );

            const opParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "operations"
            );

            // ✅ Operations always keep their own parts
            opParts.forEach(part => {
              const opEmp = empMap[part.taskownerid];
              if (opEmp) opEmp.hardwarePartsOngoing.push(part);
            });

            // ✅ Decide PM entry
            if (pmParts.length && opParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push(
                Number(highestOP.quantity) > Number(highestPM.quantity)
                  ? { ...highestOP }
                  : { ...highestPM }
              );

            } else if (!pmParts.length && opParts.length) {
              // 🔥 OP only → clone to PM
              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestOP });

            } else if (pmParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestPM });
            }
          });

          // 7️⃣ OPTIONAL: replace original newHardwareParts with processed data
          assignedData.forEach(e => {
            e.newHardwareParts = e.hardwarePartsOngoing;
          });
        }

                return {
                    ...project,
                    AssignedPersonData: assignedData,
                    managerData,
                    employeeData,
                };
            });

            // setUnifitedList(mergedData);
            setUnifitedList([
                {
                    ProjectName: allrowData?.[0]?.ProjectName,
                    quotationId: allrowData?.[0]?.newProject?.quotationId ?? allrowData?.[0]?.oldProject?.quotationId ?? null,
                    Projects: mergedData
                }
            ]);

        }
    }, [allrowData, employees]);

    return unifitedList;
};

export default useMatchedOperationsData;
